import { isEqual } from "lodash";

import { is } from "app/pages/sales-intelligence/utils/type-guards";

import type {
    FilterValueLifecycleStatus,
    IFilterPanelBaseFilterDeps,
    IFilterPanelBaseFilterModel,
} from "../types/filters";
import type { FilterValueDelta } from "../types/misc";

export class FilterPanelBaseFilter<
    DTOValue,
    O = readonly string[],
    LocalValue = DTOValue | undefined,
    FiltersDTO = Record<string, unknown>,
> implements IFilterPanelBaseFilterModel<DTOValue, O, LocalValue, FiltersDTO> {
    readonly options: O;
    readonly initialDtoValue?: DTOValue;
    readonly initialLocalValue?: LocalValue;

    private defaultValueDelta: readonly LocalValue[] = [];

    constructor(deps: IFilterPanelBaseFilterDeps<DTOValue, O>) {
        const { options, initialDtoValue } = deps;

        this.initialDtoValue = initialDtoValue;
        this.options = options;
    }

    getInitialDtoValue(): DTOValue | undefined {
        return this.initialDtoValue;
    }

    getEmptyValue(): DTOValue | undefined {
        return this.initialDtoValue;
    }

    isFilterDirty(currentValue: DTOValue | undefined): boolean {
        return this.getInitialDtoValue() !== currentValue;
    }

    getOptions(): O {
        return this.options;
    }

    toApiValue(value: LocalValue): DTOValue | undefined {
        return value as unknown as DTOValue;
    }

    fromApiValue(value?: DTOValue): LocalValue {
        return value as unknown as LocalValue;
    }

    isValueEmpty(value?: DTOValue): boolean {
        return (
            (Array.isArray(value) && !value.length) ||
            (!!value && typeof value === "object" && !Object.keys(value).length)
        );
    }

    affectValue(_: FiltersDTO, value: DTOValue | undefined): DTOValue | undefined {
        return value;
    }

    isDisabled(_: FiltersDTO): boolean {
        return false;
    }

    calculateValueDelta(_previousValue?: LocalValue, _newValue?: LocalValue): FilterValueDelta {
        return { deleted: this.defaultValueDelta };
    }

    calculateValueDeltaFromDto(previousApiValue?: DTOValue, newApiValue?: DTOValue): FilterValueDelta {
        const previousLocalValue = this.fromApiValue(previousApiValue);
        const newLocalValue = this.fromApiValue(newApiValue);

        return this.calculateValueDelta(previousLocalValue, newLocalValue);
    }

    getValueLifecycleStatus<V = LocalValue>(value?: V, valueDelta?: FilterValueDelta<V>): FilterValueLifecycleStatus {
        const isRemoved =
            !is.undefined(value) &&
            !!valueDelta?.deleted?.length &&
            valueDelta.deleted.some((item) => isEqual(item, value));

        return isRemoved ? "removed" : "added";
    }
}
